import { CURRENCY_OPTIONS, type CurrencyOption, QUALITY_CURRENCY_OPTIONS } from 'api';
import { useTranslation } from 'i18n';
import { useMemo } from 'react';

import type { WithOptional } from '../../utils/types';
import { AutocompleteField } from '../AutocompleteField/AutocompleteField';
import { SelectField, type SelectFieldProps } from '../SelectField/SelectField';

export type CurrencySelectFieldProps = Omit<
  WithOptional<SelectFieldProps, 'name' | 'required' | 'label'>,
  'options' | 'variant'
> & {
  variant?: 'quality' | 'trade';
};

type QualityCurrencyOption = CurrencyOption & {
  group: string;
};
const PRIORITIZED_CURRENCIES = ['USD', 'EUR', 'GBP', 'MXN', 'RSD'];

export const CurrencySelectField = ({
  variant = 'trade',
  name = 'currency',
  required = true,
  label = 'Currency',
  ...rest
}: CurrencySelectFieldProps) => {
  const { t } = useTranslation('common');

  const qualityOptions = useMemo(() => {
    const prioritizedOptions = PRIORITIZED_CURRENCIES.flatMap((currency) =>
      QUALITY_CURRENCY_OPTIONS.filter((option) => option.value === currency).map((option) => ({
        ...option,
        group: t('currencySelectField.groups.suggested'),
      }))
    );
    const remainingOptions = QUALITY_CURRENCY_OPTIONS.filter(
      (option) => !PRIORITIZED_CURRENCIES.includes(option.value)
    ).map((option) => ({
      ...option,
      group: t('currencySelectField.groups.other'),
    }));

    return [...prioritizedOptions, ...remainingOptions];
  }, [t]);

  if (variant === 'quality') {
    return (
      <AutocompleteField<QualityCurrencyOption>
        autoFocus={rest.autoFocus}
        disabled={rest.disabled}
        fullWidth={rest.fullWidth}
        helperText={rest.helperText}
        label={label}
        size={rest.size}
        sx={rest.sx}
        disableClearable={true}
        name={name}
        required={required}
        options={qualityOptions}
        groupBy={(option) => option.group}
        isOptionEqualToValue={(option, value) => option.value === value.value}
      />
    );
  }

  return <SelectField label={label} name={name} required={required} options={CURRENCY_OPTIONS} {...rest} />;
};
